import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CHAT_W, CHAT_H, CHAT_BLUE } from './constants';
import { API_BASE } from './lobbyReducer';
import { formatChatTime, getInitials, avatarBg } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────
// MessageThread — shared bubble renderer
// ─────────────────────────────────────────────────────────────────────────────
function MessageThread({ messages, loadingMsgs, channelId, authorName }) {
  if (loadingMsgs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
        <CircularProgress size={22} sx={{ color: CHAT_BLUE }} />
      </Box>
    );
  }
  if (!channelId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, opacity: 0.45 }}>
        <Box sx={{ fontSize: 32 }}>💬</Box>
        <Box sx={{ fontSize: '0.8rem', color: '#9ea8b3', textAlign: 'center', px: 3 }}>Save the lobby to activate this chat.</Box>
      </Box>
    );
  }
  const visible = messages.filter(m => !m.isDeleted);
  if (visible.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, opacity: 0.45 }}>
        <Box sx={{ fontSize: 32 }}>💬</Box>
        <Box sx={{ fontSize: '0.8rem', color: '#9ea8b3' }}>No messages yet. Say hello!</Box>
      </Box>
    );
  }
  return (
    <>
      {visible.map((msg, i, arr) => {
        const isOwn      = msg.authorName === authorName;
        const prevMsg    = arr[i - 1];
        const showAvatar = !isOwn && (i === 0 || prevMsg?.authorName !== msg.authorName);
        return (
          <Box
            key={msg._id}
            sx={{
              display: 'flex',
              flexDirection: isOwn ? 'row-reverse' : 'row',
              alignItems: 'flex-end',
              gap: 0.75,
              mt: showAvatar && !isOwn ? 1.25 : 0.25,
            }}
          >
            <Box sx={{ width: 30, flexShrink: 0 }}>
              {!isOwn && showAvatar && (
                <Box sx={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: avatarBg(msg.authorName),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.65rem', fontWeight: 700, color: '#fff',
                }}>
                  {getInitials(msg.authorName)}
                </Box>
              )}
            </Box>
            <Box sx={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start' }}>
              {showAvatar && !isOwn && (
                <Box sx={{ fontSize: '0.68rem', color: '#9ea8b3', mb: 0.3, px: 0.75 }}>
                  {msg.authorName}
                </Box>
              )}
              <Box sx={{
                px: 1.5, py: 0.9,
                borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                background: isOwn ? CHAT_BLUE : '#f0f4f8',
                color: isOwn ? '#fff' : '#1a1a2e',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}>
                {msg.message}
              </Box>
              <Box sx={{ fontSize: '0.62rem', color: '#c4cdd8', mt: 0.35, px: 0.75 }}>
                {formatChatTime(msg.createdAt)}
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ComposeBar — shared text input, mobile uses 16px to prevent iOS zoom
// ─────────────────────────────────────────────────────────────────────────────
function ComposeBar({ channelId, composing, setComposing, onSend, sending, inputRef, mobile = false }) {
  if (!channelId) return null;
  return (
    <Box sx={{
      flexShrink: 0,
      background: '#fff',
      borderTop: '1px solid #edf0f5',
      px: mobile ? 1.5 : 1.25,
      py: mobile ? 1.25 : 1,
      display: 'flex', alignItems: 'flex-end',
      gap: mobile ? 1 : 0.75,
    }}>
      <Box sx={{
        flex: 1,
        background: '#f5f7fa',
        borderRadius: '22px',
        px: 1.75,
        py: mobile ? 1 : 0.75,
        border: '1.5px solid transparent',
        transition: 'border-color 0.15s',
        '&:focus-within': { borderColor: CHAT_BLUE + '55' },
      }}>
        <textarea
          ref={inputRef}
          value={composing}
          onChange={e => setComposing(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Type a message…"
          rows={1}
          style={{
            width: '100%', border: 'none', outline: 'none',
            background: 'transparent', resize: 'none',
            // 16px minimum prevents iOS Safari from zooming on focus
            fontSize: mobile ? '16px' : '0.84rem',
            fontFamily: 'inherit', color: '#1a1a2e',
            lineHeight: 1.45, maxHeight: 96, overflowY: 'auto', display: 'block',
          }}
        />
      </Box>
      <Box
        onClick={composing.trim() && !sending ? onSend : undefined}
        sx={{
          width: mobile ? 42 : 36, height: mobile ? 42 : 36,
          borderRadius: '50%', flexShrink: 0,
          background: composing.trim() && !sending ? CHAT_BLUE : '#e8edf2',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: composing.trim() && !sending ? 'pointer' : 'default',
          transition: 'background 0.15s, transform 0.1s',
          '&:active': composing.trim() ? { transform: 'scale(0.92)' } : {},
        }}
      >
        {sending ? (
          <CircularProgress size={14} sx={{ color: '#fff' }} />
        ) : (
          <svg viewBox="0 0 24 24" width={mobile ? 18 : 15} height={mobile ? 18 : 15} fill={composing.trim() ? '#fff' : '#b0bac8'}>
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useChat — shared data-loading + send logic
// ─────────────────────────────────────────────────────────────────────────────
function useChat({ channelId, mode, participantId, authorName, paused = false }) {
  const [messages,    setMessages]    = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [composing,   setComposing]   = useState('');
  const [sending,     setSending]     = useState(false);
  const pollRef   = useRef(null);
  const inputRef  = useRef(null);
  const bottomRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!channelId) return;
    try {
      const params = mode === 'direct' && participantId
        ? `?limit=100&participantId=${encodeURIComponent(participantId)}`
        : '?limit=100';
      const res  = await fetch(`${API_BASE}/chat-channels/${channelId}/messages${params}`);
      const json = await res.json();
      if (json.success) setMessages(json.data || []);
    } catch {}
  }, [channelId, mode, participantId]);

  useEffect(() => {
    if (!channelId || paused) return;
    setLoadingMsgs(true);
    loadMessages().finally(() => setLoadingMsgs(false));
    pollRef.current = setInterval(loadMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [channelId, paused, loadMessages]);

  useEffect(() => {
    if (paused) clearInterval(pollRef.current);
  }, [paused]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!paused) {
      setTimeout(() => {
        if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 60);
    }
  }, [messages.length, paused]);

  const handleSend = useCallback(async () => {
    const text = composing.trim();
    if (!text || !channelId || sending) return;
    setComposing('');
    // Re-focus immediately so iOS does not dismiss the keyboard after clearing
    setTimeout(() => inputRef.current?.focus(), 0);
    setSending(true);
    try {
      const body = {
        message: text, authorName: authorName || 'Guest', authorType: 'participant',
        ...(mode === 'direct' && participantId ? { participantId, participantName: authorName } : {}),
      };
      const res  = await fetch(`${API_BASE}/chat-channels/${channelId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.success) setMessages(prev => [...prev, json.data]);
    } catch {}
    finally { setSending(false); }
  }, [composing, channelId, authorName, mode, participantId, sending]);

  return { messages, loadingMsgs, composing, setComposing, sending, handleSend, inputRef, bottomRef };
}

// ─────────────────────────────────────────────────────────────────────────────
// DESKTOP/TABLET: DesktopChatModal
// Centered dialog, channels list on the left, chat on the right.
// Clicking the backdrop closes the modal.
// ─────────────────────────────────────────────────────────────────────────────
export function DesktopChatModal({ openChats, onCloseAll, authorName, participantId, initialChat = null }) {
  const [activeChat, setActiveChat] = useState(initialChat);

  // Chat data owned here, re-initialised when active channel changes
  const chatData = useChat(
    activeChat
      ? { channelId: activeChat.channelId, mode: activeChat.mode || 'group', participantId, authorName }
      : { channelId: null, mode: 'group', participantId, authorName }
  );

  // Auto-select first channel if only one exists
  useEffect(() => {
    if (!activeChat && openChats.length === 1) setActiveChat(openChats[0]);
  }, [openChats]); // eslint-disable-line

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    // Backdrop
    <Box
      onClick={onCloseAll}
      sx={{
        position: 'fixed', inset: 0, zIndex: 10600,
        background: 'rgba(10,14,26,0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'desk-chat-fade 0.2s ease both',
        '@keyframes desk-chat-fade': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      {/* Dialog — stop click from bubbling to backdrop */}
      <Box
        onClick={e => e.stopPropagation()}
        sx={{
          width: '90vw', maxWidth: 860,
          height: '78vh', maxHeight: 640,
          minHeight: 440,
          display: 'flex',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.45)',
          animation: 'desk-chat-in 0.22s cubic-bezier(0.32,0.72,0,1) both',
          '@keyframes desk-chat-in': {
            from: { transform: 'scale(0.96) translateY(8px)', opacity: 0 },
            to:   { transform: 'scale(1) translateY(0)',      opacity: 1 },
          },
        }}
      >
        {/* ── Left sidebar: channel list ─────────────────────────────────── */}
        <Box sx={{
          width: 280, flexShrink: 0,
          display: 'flex', flexDirection: 'column',
          background: '#f7f8fa',
          borderRight: '1px solid #e8edf4',
        }}>
          {/* Sidebar header */}
          <Box sx={{
            px: 2.5, height: 60, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: '1px solid #e8edf4',
            background: '#f7f8fa',
          }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0a0e1a', letterSpacing: '-0.3px' }}>
              Messages
            </Typography>
            {/* Close button */}
            <Box
              onClick={onCloseAll}
              sx={{
                width: 32, height: 32, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: '#6b7585',
                transition: 'all 0.12s',
                '&:hover': { background: '#eaecf0', color: '#0a0e1a' },
              }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </Box>
          </Box>

          {/* Channel rows */}
          <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 99 } }}>
            {openChats.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1.5, opacity: 0.4, px: 3 }}>
                <Box sx={{ fontSize: 32 }}>💬</Box>
                <Box sx={{ fontSize: '0.82rem', color: '#9ea8b3', textAlign: 'center' }}>No chat channels available</Box>
              </Box>
            ) : openChats.map(cw => {
              const isActive = activeChat?.id === cw.id;
              return (
                <Box
                  key={cw.id}
                  onClick={() => setActiveChat(cw)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.4,
                    cursor: 'pointer',
                    borderBottom: '1px solid #eef0f4',
                    background: isActive ? `${CHAT_BLUE}12` : 'transparent',
                    borderLeft: isActive ? `3px solid ${CHAT_BLUE}` : '3px solid transparent',
                    transition: 'background 0.12s',
                    '&:hover': { background: isActive ? `${CHAT_BLUE}12` : '#eef0f4' },
                  }}
                >
                  {/* Avatar */}
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                    background: isActive
                      ? `linear-gradient(135deg, ${CHAT_BLUE}cc, ${CHAT_BLUE})`
                      : `linear-gradient(135deg, #c8d4e8, #aabacf)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg viewBox="0 0 24 24" width="19" height="19" fill="#fff">
                      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                    </svg>
                  </Box>
                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: '0.88rem', fontWeight: isActive ? 700 : 600,
                      color: isActive ? CHAT_BLUE : '#1a1a2e',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      lineHeight: 1.3,
                    }}>
                      {cw.title || 'Live Chat'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3' }}>Live</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── Right pane: chat ──────────────────────────────────────────── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', minWidth: 0 }}>
          {activeChat ? (
            <>
              {/* Chat header */}
              <Box sx={{
                height: 60, flexShrink: 0,
                display: 'flex', alignItems: 'center',
                px: 2.5, gap: 1.5,
                borderBottom: '1px solid #eef0f4',
                background: '#fff',
              }}>
                <Box sx={{
                  width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                  background: `linear-gradient(135deg, ${CHAT_BLUE}cc, ${CHAT_BLUE})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="#fff">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                  </svg>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0a0e1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                    {activeChat.title || 'Live Chat'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3' }}>Live</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Messages */}
              <Box sx={{
                flex: 1, minHeight: 0, overflowY: 'auto',
                px: 2.5, py: 2,
                display: 'flex', flexDirection: 'column',
                background: '#fafbfd',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 99 },
              }}>
                <MessageThread
                  messages={chatData.messages}
                  loadingMsgs={chatData.loadingMsgs}
                  channelId={activeChat.channelId}
                  authorName={authorName}
                />
                <div ref={chatData.bottomRef} style={{ height: 8 }} />
              </Box>

              {/* Compose */}
              <ComposeBar
                channelId={activeChat.channelId}
                composing={chatData.composing}
                setComposing={chatData.setComposing}
                onSend={chatData.handleSend}
                sending={chatData.sending}
                inputRef={chatData.inputRef}
              />
            </>
          ) : (
            /* Empty state when no channel selected */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.35, background: '#fafbfd' }}>
              <svg viewBox="0 0 24 24" width="48" height="48" fill="#9ea8b3">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
              </svg>
              <Typography sx={{ fontSize: '0.9rem', color: '#9ea8b3', fontWeight: 500 }}>
                Select a conversation
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE: ChannelsListScreen
// ─────────────────────────────────────────────────────────────────────────────
function ChannelsListScreen({ chatWindows, onSelectChannel, onClose }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5,
        height: 62, flexShrink: 0,
        borderBottom: '1px solid #f0f4f8',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}>
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#0a0e1a', letterSpacing: '-0.4px' }}>
          Messages
        </Typography>
        <Box
          onClick={onClose}
          sx={{
            width: 36, height: 36, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            background: '#f5f7fa',
            border: '1px solid #e8edf2',
            color: '#5a6474',
            WebkitTapHighlightColor: 'transparent',
            transition: 'all 0.12s',
            '&:active': { background: '#eaecf0', transform: 'scale(0.92)' },
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </Box>
      </Box>

      {/* Channel list */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {chatWindows.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60%', gap: 2, opacity: 0.4 }}>
            <Box sx={{ fontSize: 40 }}>💬</Box>
            <Box sx={{ fontSize: '0.88rem', color: '#9ea8b3', textAlign: 'center', px: 4 }}>No chat channels available</Box>
          </Box>
        ) : (
          chatWindows.map((cw, i) => (
            <Box
              key={cw.id}
              onClick={() => onSelectChannel(cw)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.75,
                px: 2.5, py: 1.5,
                cursor: 'pointer',
                borderBottom: '1px solid #f5f7fb',
                transition: 'background 0.1s',
                WebkitTapHighlightColor: 'transparent',
                '&:active': { background: '#f5f7fa' },
              }}
            >
              <Box sx={{
                width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${CHAT_BLUE}bb, ${CHAT_BLUE})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 2px 10px ${CHAT_BLUE}30`,
              }}>
                <svg viewBox="0 0 24 24" width="22" height="22" fill="#fff">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                </svg>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                  fontSize: '0.97rem', fontWeight: 700, color: '#0a0e1a',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  lineHeight: 1.3,
                }}>
                  {cw.title || 'Live Chat'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#9ea8b3' }}>Live</Typography>
                </Box>
              </Box>
              <Box sx={{ flexShrink: 0, color: '#d0d7e2' }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                </svg>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MOBILE: MobileChatModal
// The header and compose bar are children of the fixed overlay directly —
// NOT inside any transformed element — so iOS Safari never traps them inside
// a transform stacking context. Only the message body slides left/right.
// ─────────────────────────────────────────────────────────────────────────────
const CHAT_HEADER_H = 62;

export function MobileChatModal({ openChats, onCloseAll, authorName, participantId, initialChat = null }) {
  const [activeChat, setActiveChat] = useState(initialChat);
  const [slide, setSlide]           = useState(initialChat ? 'chat' : 'list');

  // Live-measure compose bar so message area bottom tracks it correctly
  const composeRef = useRef(null);
  const [composeH, setComposeH] = useState(64);
  useEffect(() => {
    const el = composeRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setComposeH(e.contentRect.height));
    ro.observe(el);
    setComposeH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  // All chat data owned here — stays alive across slide transitions
  const chatData = useChat(
    activeChat
      ? { channelId: activeChat.channelId, mode: activeChat.mode || 'group', participantId, authorName }
      : { channelId: null, mode: 'group', participantId, authorName }
  );

  const goToChat = useCallback((cw) => { setActiveChat(cw); setSlide('chat'); }, []);
  const goToList = useCallback(() => { setActiveChat(null); setSlide('list'); }, []);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Prevent pinch-zoom + resize layout on keyboard (not just pan)
  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    const prevContent = meta ? meta.getAttribute('content') : null;
    if (!meta) { meta = document.createElement('meta'); meta.name = 'viewport'; document.head.appendChild(meta); }
    meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, interactive-widget=resizes-content');
    return () => {
      if (prevContent !== null) meta.setAttribute('content', prevContent);
      else meta.setAttribute('content', 'width=device-width, initial-scale=1');
    };
  }, []);

  const listX = slide === 'chat' ? '-100%' : '0%';
  const chatX = slide === 'chat' ? '0%'    : '100%';
  const inChat = slide === 'chat';
  const SLIDE_TRANSITION = 'transform 0.28s cubic-bezier(0.4,0,0.2,1)';

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 10600,
      background: '#fff',
      overflow: 'hidden',
      touchAction: 'pan-y',
      overscrollBehavior: 'none',
      animation: 'mob-chat-in 0.28s cubic-bezier(0.32,0.72,0,1) both',
      '@keyframes mob-chat-in': {
        from: { transform: 'translateY(100%)' },
        to:   { transform: 'translateY(0)' },
      },
    }}>

      {/* ── LAYER 1: Channels list (slides out left when chat opens) ── */}
      <Box sx={{
        position: 'absolute', inset: 0,
        transform: `translateX(${listX})`,
        transition: SLIDE_TRANSITION,
        willChange: 'transform',
        background: '#fff',
        pointerEvents: inChat ? 'none' : 'all',
      }}>
        <ChannelsListScreen chatWindows={openChats} onSelectChannel={goToChat} onClose={onCloseAll} />
      </Box>

      {/* ── LAYER 2: Message body only — slides in from right ── */}
      {/* top/bottom offsets leave room for the header and compose bar */}
      <Box sx={{
        position: 'absolute',
        top: CHAT_HEADER_H, left: 0, right: 0,
        bottom: composeH,
        transform: `translateX(${chatX})`,
        transition: SLIDE_TRANSITION,
        willChange: 'transform',
        pointerEvents: inChat ? 'all' : 'none',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        px: 1.5, py: 1.5,
        display: 'flex', flexDirection: 'column',
        background: '#fafbfd',
        boxSizing: 'border-box',
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 99 },
      }}>
        {activeChat && (
          <>
            <MessageThread
              messages={chatData.messages}
              loadingMsgs={chatData.loadingMsgs}
              channelId={activeChat.channelId}
              authorName={authorName}
            />
            <div ref={chatData.bottomRef} style={{ height: 8 }} />
          </>
        )}
      </Box>

      {/* ── LAYER 3: Chat header — direct child of fixed overlay, no transform ancestor ── */}
      {/* Slides with the chat panel but is NOT inside it, so iOS cannot trap it. */}
      <Box sx={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: CHAT_HEADER_H,
        zIndex: 10,
        transform: `translateX(${chatX})`,
        transition: SLIDE_TRANSITION,
        willChange: 'transform',
        display: 'flex', alignItems: 'center',
        px: 1, gap: 0.5,
        borderBottom: '1px solid #f0f4f8',
        background: '#fff',
        boxSizing: 'border-box',
        pointerEvents: inChat ? 'all' : 'none',
      }}>
        {/* Back */}
        <Box onClick={goToList} sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: CHAT_BLUE, flexShrink: 0, WebkitTapHighlightColor: 'transparent', '&:active': { background: `${CHAT_BLUE}14`, transform: 'scale(0.92)' }, transition: 'all 0.12s' }}>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
        </Box>
        {/* Avatar */}
        <Box sx={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${CHAT_BLUE}bb, ${CHAT_BLUE})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="#fff"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" /></svg>
        </Box>
        {/* Title */}
        <Box sx={{ flex: 1, minWidth: 0, ml: 0.75 }}>
          <Typography sx={{ fontSize: '0.975rem', fontWeight: 700, color: '#0a0e1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
            {activeChat?.title || 'Live Chat'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#9ea8b3', lineHeight: 1 }}>Live</Typography>
          </Box>
        </Box>
        {/* Close */}
        <Box onClick={onCloseAll} sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#f5f7fa', border: '1px solid #e8edf2', color: '#5a6474', mr: 1, WebkitTapHighlightColor: 'transparent', '&:active': { background: '#eaecf0', transform: 'scale(0.92)' }, transition: 'all 0.12s' }}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
        </Box>
      </Box>

      {/* ── LAYER 4: Compose bar — direct child of fixed overlay, no transform ancestor ── */}
      {/* position:absolute bottom:0 — browser moves it above keyboard via interactive-widget */}
      <Box ref={composeRef} sx={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        zIndex: 10,
        transform: `translateX(${chatX})`,
        transition: SLIDE_TRANSITION,
        willChange: 'transform',
        pointerEvents: inChat ? 'all' : 'none',
      }}>
        <ComposeBar
          channelId={activeChat?.channelId ?? null}
          composing={chatData.composing}
          setComposing={chatData.setComposing}
          onSend={chatData.handleSend}
          sending={chatData.sending}
          inputRef={chatData.inputRef}
          mobile
        />
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FloatingChatManager
// Mobile  → MobileChatModal (full-screen slide navigation)
// Desktop → DesktopChatModal (centred dialog, sidebar + chat pane)
// ─────────────────────────────────────────────────────────────────────────────
export function FloatingChatManager({ openChats, minimized, onClose, onMinimize, authorName, participantId, isMobile = false }) {
  if (!openChats.length) return null;

  const closeAll = () => openChats.forEach(cw => onClose(cw.id));

  if (isMobile) {
    return (
      <MobileChatModal
        openChats={openChats}
        initialChat={openChats[openChats.length - 1]}
        onCloseAll={closeAll}
        authorName={authorName}
        participantId={participantId}
      />
    );
  }

  return (
    <DesktopChatModal
      openChats={openChats}
      initialChat={openChats[openChats.length - 1]}
      onCloseAll={closeAll}
      authorName={authorName}
      participantId={participantId}
    />
  );
}